import { useEffect, useRef, useState } from 'react';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import { Map3Controls } from './Map3Controls';
import { SaveAndRefresh } from '../../components_misc/SaveAndRefresh';
import { ShareButton } from '../../components_share/ShareButton';
import { RequestAccessButton } from '../../components_share/RequestAccessButton';
import { StarIcon } from '@heroicons/react/24/outline';
import { Button } from '../../components_catalyst/button';
import { updateUser, updateMilestoneMap } from '../../store/map3PendingSlice';
import { UserProfile1 } from '@mm-shared/types/UserProfile1';
import { EmbeddedTreeView } from '../../embedded_treeview/EmbeddedTreeView';
import { ActiveCollaborators } from '../../collaboration/components/ActiveCollaborators';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '../../utils/getPermissionsForRole';
import { selectUserProfile } from '../../store/selectors';
import { Map3Settings } from '../dev/Map3Settings';
import { Map3RelativesWrapper } from '../../map_dependencies/components/Map3RelativesWrapper';
import { Map3Dependencies } from '../../components_dependencies/Map3Dependencies';
import {
  Map3WorkspaceContext,
  Map3WorkspaceContextValue,
} from './Map3WorkspaceContext';

/**
 * Map3Workspace - Container component for the milestone map workspace
 *
 * View Parameter System:
 * The workspace supports different views of the same map data through URL parameters:
 * - Default view (no parameter): Standard table view of objectives and statuses
 * - Dependencies view (?view=dependencies): Visualization of dependencies using ReactFlow
 *
 * This approach:
 * 1. Uses URL parameters for view state, making views shareable and bookmarkable
 * 2. Keeps view state in sync between Redux and URL
 * 3. Allows easy addition of new views (e.g., ?view=relatives) without modifying core components
 * 4. Maintains clean separation between base functionality and enhanced views
 *
 * Views are toggled through Map3Controls and managed in map3UISlice.
 */
/**
 * Valid workspace view values for the ?view query param.
 * undefined/null means the default table view (Map3).
 */
export type WorkspaceView =
  | 'settings'
  | 'relatives'
  | 'treeview'
  | 'dependencies'
  | 'playlists'
  | 'help-videos';

export function Map3Workspace() {
  const dispatch = useDispatch<AppDispatch>();
  const reactRouterParams = useParams();
  const mapShortId = reactRouterParams.mapShortId;
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view') as WorkspaceView | null;

  // Only keep logic for default map view, star, and unrelated features
  const isLoading = useSelector((state: RootState) => state.map3Data.isLoading);
  const userProfile = useSelector(selectUserProfile);
  const enableSidebarTreeView = useSelector(
    (state: RootState) => state.map3UI.mapControls.enableSidebarTreeView,
  );
  const isStarred =
    userProfile.myMilestoneMaps?.find((m) => m.shortId === mapShortId)?.starred ||
    userProfile.sharedMilestoneMaps?.find((m) => m.shortId === mapShortId)?.starred ||
    false;
  const handleStar = async () => {
    try {
      let updatedMyMaps = userProfile.myMilestoneMaps || [];
      let updatedSharedMaps = userProfile.sharedMilestoneMaps || [];
      const inMyMaps = updatedMyMaps.find((m) => m.shortId === mapShortId);
      const inSharedMaps = updatedSharedMaps.find((m) => m.shortId === mapShortId);

      if (inMyMaps) {
        updatedMyMaps = updatedMyMaps.map((m) =>
          m.shortId === mapShortId ? { ...m, starred: !m.starred } : m,
        );
      } else if (inSharedMaps) {
        updatedSharedMaps = updatedSharedMaps.map((m) =>
          m.shortId === mapShortId ? { ...m, starred: !m.starred } : m,
        );
      }

      await dispatch(
        updateUser({
          uid: userProfile.uid,
          updates: {
            myMilestoneMaps: updatedMyMaps,
            sharedMilestoneMaps: updatedSharedMaps,
          },
        }),
      );
    } catch (error) {
      console.error('Failed to update star status:', error);
    }
  };
  const milestoneMap = useSelector((state: RootState) =>
    state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId),
  );

  // Check user's role for conditional rendering
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canShare, canViewMap } = getPermissionsForRole(userRole);

  const warning = !mapShortId
    ? 'You need a map ID in the URL'
    : !milestoneMap
      ? 'Map not found'
      : '';
  const title = milestoneMap?.title || 'Untitled Map';

  // Effect to update accessedMilestoneMaps when map is loaded
  useEffect(() => {
    if (!isLoading && milestoneMap && userProfile?.uid) {
      // Check if we've already recorded this map access today
      const today = new Date().toISOString().split('T')[0]; // Get YYYY-MM-DD
      const existingAccess = userProfile.accessedMilestoneMaps?.find(
        (map) =>
          map.shortId === mapShortId && map.lastAccessed && map.lastAccessed.startsWith(today),
      );

      // Only update if we haven't recorded access today
      if (!existingAccess) {
        // Create a new array with the existing maps (excluding current map if it exists)
        const existingMaps = (userProfile.accessedMilestoneMaps || []).filter(
          (map) => map.shortId !== mapShortId,
        );

        // Create new map access entry
        const newMapAccess = {
          uuid: milestoneMap.uuid,
          shortId: milestoneMap.shortId,
          title: milestoneMap.title || '',
          // TODO: Harden this. Throw an error if there is no owner name. This really should never happen.
          // This fallback to userProfile.userName is a hack to keep dummy data working in Alpha.
          // Once we have more real data working consistently, we should remove this fallback.
          ownerName: milestoneMap.owner?.name || userProfile.userName,
          lastAccessed: new Date().toISOString(),
          pinned:
            userProfile.accessedMilestoneMaps?.find((m) => m.shortId === mapShortId)?.pinned ||
            false,
        };

        // Combine into new array
        const updatedAccessedMaps = [...existingMaps, newMapAccess];

        dispatch(
          updateUser({
            uid: userProfile.uid,
            updates: {
              accessedMilestoneMaps: updatedAccessedMaps,
            },
          }),
        );
      }
    }
  }, [dispatch, isLoading, mapShortId, milestoneMap, userProfile]);

  // Effect to update map's usersWithAccess with joinedDate and lastAccessedDate
  useEffect(() => {
    if (!isLoading && milestoneMap && userProfile?.uid) {
      // Check if current user is in the map's usersWithAccess array
      const userAccess = milestoneMap.usersWithAccess?.find((user) => user.uid === userProfile.uid);

      if (userAccess) {
        const now = new Date().toISOString();
        let needsUpdate = false;
        const updatedUserAccess = { ...userAccess };

        // Set joinedDate if it doesn't exist (first time joining)
        if (!userAccess.joinedDate) {
          updatedUserAccess.joinedDate = now;
          needsUpdate = true;
        }

        // Always update lastAccessedDate (but only once per day to avoid excessive updates)
        const today = new Date().toISOString().split('T')[0];
        const lastAccessedToday =
          userAccess.lastAccessedDate && userAccess.lastAccessedDate.startsWith(today);

        if (!lastAccessedToday) {
          updatedUserAccess.lastAccessedDate = now;
          needsUpdate = true;
        }

        // Update the map if changes are needed
        if (needsUpdate) {
          const updatedUsersWithAccess = milestoneMap.usersWithAccess?.map((user) =>
            user.uid === userProfile.uid ? updatedUserAccess : user,
          );

          dispatch(
            updateMilestoneMap({
              uuid: milestoneMap.uuid,
              updates: {
                usersWithAccess: updatedUsersWithAccess,
              },
            }),
          );
        }
      }
    }
  }, [dispatch, isLoading, milestoneMap, userProfile?.uid]);

  // Named views use the hidden-sibling pattern: Outlet is always rendered so Map3
  // stays mounted (preserving mr-aqs.20 hidden-Outlet stability and mr-aqs.17.1 memo).
  // Each named view component is a sibling — toggled visible/hidden by the ?view param.
  // Outlet is hidden when any named view is active; named views are hidden when inactive.
  const namedViews = ['settings', 'relatives', 'treeview', 'dependencies'] as const;
  const isNamedView = namedViews.includes(view as (typeof namedViews)[number]);

  // -------------------------------------------------------------------------
  // mr-4oag: viewVisibilityVersion — reset Framer Motion projection cache
  //
  // When the Outlet wrapper transitions from hidden (isNamedView=true) to visible
  // (isNamedView=false), Framer Motion's Reorder.Group sees stale (0,0,0,0)
  // projection boxes from when it was hidden, causing a fly-in animation.
  //
  // Fix: increment viewVisibilityVersion on each hidden→visible transition.
  // Map3Tbody keys its Reorder.Group on this value so Framer Motion discards
  // its stale projection cache and remounts the Reorder subtree cleanly —
  // without replaying the fly-in animation.
  //
  // This does NOT remount Map3 itself (mr-aqs.20 + mr-aqs.17.1 preserved):
  // only the Reorder.Group subtree inside Map3Tbody is remounted.
  // -------------------------------------------------------------------------
  const [viewVisibilityVersion, setViewVisibilityVersion] = useState(0);
  const prevIsNamedViewRef = useRef(isNamedView);
  useEffect(() => {
    const wasNamedView = prevIsNamedViewRef.current;
    prevIsNamedViewRef.current = isNamedView;
    // Increment only on hidden→visible transition (namedView: true → false)
    if (wasNamedView && !isNamedView) {
      setViewVisibilityVersion((v) => v + 1);
    }
  }, [isNamedView]);

  const workspaceContextValue: Map3WorkspaceContextValue = { viewVisibilityVersion };

  if (!mapShortId) return null;

  // Determine whether the map content (Outlet/Map3) should be visible.
  // The Outlet is ALWAYS rendered so Map3 is never unmounted during navigation
  // (unmounting Map3 resets all its local state — objectivesOnMap, undoStack, etc. —
  // causing a visible flash of an empty grid on every first status-cell click).
  // Instead, we hide/show it via CSS visibility while keeping it in the DOM.
  const mapContentVisible = !isLoading && canViewMap;

  return (
    <Map3WorkspaceContext.Provider value={workspaceContextValue}>
    <section id="Map3Workspace" aria-label="Milestone map workspace">
      {/* Loading and access-denied states: shown as inline messages above the (hidden) map */}
      {isLoading && (
        <div role="status" aria-live="polite" className="text-center py-4">
          {'Loading milestone map data...'}
        </div>
      )}
      {!isLoading && !canViewMap && (
        <div role="status" aria-live="polite" className="text-center py-4">
          {'Access denied'}
        </div>
      )}

      {/*
        Map content: always in the DOM once mapShortId is set.
        Visibility is toggled via `hidden` so that Map3 (rendered via Outlet) never
        unmounts during transient loading or permission checks. This prevents the
        map grid from flashing empty when isLoading briefly becomes true.
      */}
      <div hidden={!mapContentVisible} aria-hidden={!mapContentVisible || undefined}>
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h1 id="map-title" className="text-xl font-semibold">
              {title}
            </h1>
            <Button
              plain
              onClick={handleStar}
              aria-label={isStarred ? 'Remove star' : 'Star map'}
              aria-pressed={isStarred}
            >
              <StarIcon
                className={`h-5 w-5 ${isStarred ? 'text-yellow-400 fill-current' : 'text-zinc-500'}`}
                aria-hidden="true"
              />
            </Button>
            <SaveAndRefresh />
          </div>
          <div className="flex items-center gap-3">
            <ActiveCollaborators />
            {/* Show ShareButton for users who can share, RequestAccessButton for others with access */}
            {canShare ? (
              <ShareButton />
            ) : userRole ? (
              <RequestAccessButton />
            ) : /* User has no access to the map - don't show any sharing options */
            null}
          </div>
        </div>

        <Map3Controls />
        {warning && (
          <div role="alert" className="text-red-600 mb-4">
            {warning}
          </div>
        )}

        {/*
          View switching (hidden-sibling pattern):
          The Outlet is ALWAYS rendered so Map3 is never unmounted when switching views.
          This preserves mr-aqs.20 hidden-Outlet mount stability and mr-aqs.17.1 React.memo.
          Named views (settings, relatives, treeview, dependencies) are siblings rendered
          alongside the Outlet — each toggled visible/hidden by the ?view param.
          playlists and help-videos don't require a named view component; Map3 handles
          them internally, so the Outlet stays visible for those values too.
        */}

        {/* Default / Outlet view — always in DOM, hidden when a named view is active */}
        <div
          hidden={isNamedView}
          aria-hidden={isNamedView || undefined}
        >
          {enableSidebarTreeView && !isNamedView ? (
            <div className="flex gap-4 h-full">
              {/* Sidebar Tree View - 25% width */}
              <div className="w-1/3 min-w-[300px] border border-slate-200 rounded-md overflow-hidden">
                <EmbeddedTreeView milestoneMapId={mapShortId} />
              </div>
              {/* Main Content - 75% width */}
              <div className="flex-1 relative overflow-x-auto border border-slate-200 rounded-md">
                <div className="min-w-fit">
                  <Outlet />
                </div>
              </div>
            </div>
          ) : (
            <div className="relative w-full overflow-x-auto border border-slate-200 rounded-md">
              <div className="min-w-fit">
                <Outlet />
              </div>
            </div>
          )}
        </div>

        {/* Named view: settings */}
        <div hidden={view !== 'settings'} aria-hidden={view !== 'settings' || undefined}>
          <div className="relative w-full overflow-x-auto border border-slate-200 rounded-md">
            <div className="min-w-fit">
              <Map3Settings />
            </div>
          </div>
        </div>

        {/* Named view: relatives */}
        {/*
          Overflow wrapper matches the Outlet pattern (overflow-x-auto + border + rounded-md).
          No tabIndex on this wrapper — the inner RelativesSharedTimeline / RelativesStacked
          components already have tabIndex={0} + onKeyDown for keyboard scroll (mr-aqs.17.2).
          Adding another tabIndex here would create a double tab stop (WCAG 2.1.1).
          role="region" + aria-label announces the scroll region to screen readers at the
          outer boundary; keyboard users access it via the inner component's existing focus.
        */}
        <div hidden={view !== 'relatives'} aria-hidden={view !== 'relatives' || undefined}>
          <div
            role="region"
            aria-label="Relatives view scroll region"
            className="relative w-full overflow-x-auto border border-slate-200 rounded-md"
          >
            <div className="min-w-fit">
              <Map3RelativesWrapper />
            </div>
          </div>
        </div>

        {/* Named view: treeview (full-page, not sidebar) */}
        <div hidden={view !== 'treeview'} aria-hidden={view !== 'treeview' || undefined}>
          <div className="relative w-full overflow-x-auto border border-slate-200 rounded-md">
            <div className="min-w-fit">
              <EmbeddedTreeView milestoneMapId={mapShortId} />
            </div>
          </div>
        </div>

        {/* Named view: dependencies */}
        <div hidden={view !== 'dependencies'} aria-hidden={view !== 'dependencies' || undefined}>
          <div className="relative w-full overflow-x-auto border border-slate-200 rounded-md">
            <div className="min-w-fit">
              <Map3Dependencies />
            </div>
          </div>
        </div>
      </div>
    </section>
    </Map3WorkspaceContext.Provider>
  );
}

/**
 * Utility: Returns all map uuids for which the user has access.
 * @param userProfile
 * @returns Array of map uuids
 */
function getAccessibleMapUuids(userProfile: UserProfile1): string[] {
  const myMaps = userProfile?.myMilestoneMaps || [];
  const sharedMaps = userProfile?.sharedMilestoneMaps || [];
  const accessibleUuids = [
    ...myMaps.map((m: any) => m.uuid),
    ...sharedMaps.map((m: any) => m.uuid),
  ];
  return accessibleUuids;
}

/**
 * Utility: resolve any shortId or uuid to uuid from a list of uuids.
 * Returns the first uuid that starts with the given id (to support both shortId and uuid matching).
 * @param id
 * @param uuids
 * @returns uuid
 */
function resolveToUuid(id: string, uuids: string[]): string | undefined {
  return uuids.find((uuid) => uuid.startsWith(id));
}
